import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import {
  employeeCountForAdhocPrograms,
  exportCSV,
  formatDateinDDMMMYYYAndTimeWithoutSeparator,
  formatDateSorting,
  formatIDForGifting,
  formatPaymentRequestStatusEnumForFilter,
  getCurrentDate,
  includesOption,
  indexOfOption,
  isProgramEnded,
  searchWithName,
} from '../../../helpers';
import DataTables from '../../common/components/DataTables/DataTables';
import { giftingProgramHeadColumn } from '../../common/components/DataTables/components/headColumns';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  gcProgramStatusEnum,
  paymentRequestEnum,
  paymentRequestStatus,
  programStatusEnum,
  programTypeEnum,
  statusLabels,
} from '../../common/components/constants';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import StatusFilter from '../../common/components/StatusFilter';

const GiftingAdhocPlanTable = ({
  currentFlow,
  selectedWorkspace,
  fetchingPrograms,
  fetchedPrograms,
  programsData,
  getAllPrograms,
  viewOnlyMode,
}) => {
  console.log(viewOnlyMode);
  const keyFields = ['name', 'program_id'];
  const { programs = [] } = programsData;
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    // {
    //   value: [5],
    //   label: 'PLAN_ACTIVE',
    //   disabled: true,
    // },
    {
      value: [4],
      label: 'GC_COMPLETED_ENDED',
      disabled: true,
    },
    {
      value: [200],
      label: 'EXPIRED',
      disabled: true,
    },
    {
      value: [104],
      label: 'PAID',
      disabled: true,
    },
    {
      value: [101],
      label: 'PENDING',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      if (
        filter.value[0] ===
        filterStatusOptions.find(x => x.label === 'EXPIRED').value[0]
      ) {
        if (
          isProgramEnded(val) &&
          val?.payment_request?.status !== paymentRequestEnum.PAID
        ) {
          flag = true;
        }
      } else if (
        filter.value[0] ===
        filterStatusOptions.find(x => x.label === 'PAID').value[0]
      ) {
        if (
          formatPaymentRequestStatusEnumForFilter(
            val?.payment_request?.status,
          ) === filter.value[0] &&
          val.program_status !== gcProgramStatusEnum.GC_COMPLETED_ENDED
        )
          flag = true;
      } else if (
        filter.value[0] ===
        filterStatusOptions.find(x => x.label === 'PENDING').value[0]
      ) {
        if (
          formatPaymentRequestStatusEnumForFilter(
            val?.payment_request?.status,
          ) === filter.value[0] &&
          !isProgramEnded(val)
        )
          flag = true;
      } else {
        for (let i = 0; i < filter.value.length; i += 1) {
          if (val.program_status === filter.value[0]) flag = true;
        }
      }
    });
    return flag;
  };

  const statusFilter = val => {
    if (
      val.program_status !== 0 &&
      val.program_status !== 3 &&
      val.program_status !== 5
    ) {
      return true;
    }
    return false;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    if (!fetchingPrograms) {
      if (programs) {
        programs.sort(
          (a, b) =>
            formatDateSorting(b.created_on) - formatDateSorting(a.created_on),
        );
        const filteredPrograms = programs.filter(statusFilter);
        if (filteredPrograms.length === 0) {
          setDisabledSearch(true);
        }
        setSearchResults(filteredPrograms);
        setCurrentData(filteredPrograms.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingPrograms]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingPrograms) {
        const arr = searchWithName(programs, search, keyFields);
        const draftedFilterArr = arr.filter(statusFilter);
        const availableStatuses = [];
        draftedFilterArr.forEach(data => {
          if (
            isProgramEnded(data) &&
            data?.payment_request?.status !== paymentRequestEnum.PAID
          ) {
            availableStatuses.push(200);
          }
          if (!availableStatuses.includes(data.program_status)) {
            availableStatuses.push(data.program_status);
          }
          if (
            !availableStatuses.includes(
              formatPaymentRequestStatusEnumForFilter(
                data?.payment_request?.status,
              ),
            )
          ) {
            // used +100 to differentiate between program status and payment request status
            if (
              data?.payment_request?.status === paymentRequestEnum.PAID &&
              data.program_status !== gcProgramStatusEnum.GC_COMPLETED_ENDED
            )
              availableStatuses.push(
                formatPaymentRequestStatusEnumForFilter(
                  data?.payment_request?.status,
                ),
              );
            if (
              data?.payment_request?.status === paymentRequestEnum.ACTIVE &&
              !isProgramEnded(data)
            )
              availableStatuses.push(
                formatPaymentRequestStatusEnumForFilter(
                  data?.payment_request?.status,
                ),
              );
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = draftedFilterArr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(draftedFilterArr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingPrograms]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'created_on', value: 'Created on' },
      { key: 'id', value: 'ID' },
      { key: 'name', value: 'Occasion' },
      { key: 'execution_date', value: 'Execution date' },
      { key: 'gc_count', value: 'No of GC' },
      { key: 'amount', value: 'Total amount' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.name = data?.gift_card_details?.type_name;
      labelledData.id = formatIDForGifting(data?.program_id);
      labelledData.execution_date = formatDateinDDMMMYYYAndTimeWithoutSeparator(
        data?.end_date,
      );
      labelledData.created_on = formatDateinDDMMMYYYAndTimeWithoutSeparator(
        data?.created_on,
      );
      labelledData.gc_count = employeeCountForAdhocPrograms(data);
      labelledData.amount = data?.amount;
      labelledData.program_status =
        data.program_status === programStatusEnum.DRAFTED ||
        data.program_status === programStatusEnum.DELETED
          ? statusLabels[programStatusEnum[data.program_status] ?? 'default']
          : statusLabels[
              paymentRequestStatus[data?.payment_request?.status ?? 0]
            ];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `Gift Card history - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  const refreshPage = () => {
    console.log('refresh');
    getAllPrograms({
      workspaceId: selectedWorkspace.workspace_id,
      flowType: currentFlow,
      programType: programTypeEnum.PROGRAM_TYPE_ADHOC,
    });
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by ID or occasion'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by ID or occasion
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            isDisabled={disabledSearch}
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          {disabledSearch ? (
            <Download color='grey' style={downloadCSVStyle} />
          ) : (
            <Tooltip
              position='top'
              label='Download gift card history'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
                padding: '10px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedPrograms ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            columns={giftingProgramHeadColumn}
            loading={fetchingPrograms || loading}
            data={currentData}
            pagination
            totalRows={searchResults.length}
            handlePageChange={handlePageChange}
            onSort={handleSort}
            emptyStateTitle='Gift card history not found. Please try again'
          />
        )}
      </TableContainer>
    </>
  );
};

GiftingAdhocPlanTable.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  fetchedPrograms: PropTypes.bool,
  fetchingPrograms: PropTypes.bool,
  programsData: PropTypes.objectOf(PropTypes.any),
  getAllPrograms: PropTypes.func,
  viewOnlyMode: PropTypes.bool,
};

GiftingAdhocPlanTable.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD,
  selectedWorkspace: {},
  fetchedPrograms: PropTypes.bool,
  fetchingPrograms: PropTypes.bool,
  programsData: PropTypes.objectOf(PropTypes.any),
  getAllPrograms: _noop,
  viewOnlyMode: false,
};

export default GiftingAdhocPlanTable;
